import { useState, useRef, useEffect } from 'react'
import { useAiAssistant } from '../hooks/useAiAssistant'
import { t } from '../i18n/translations'

export default function AiAssistant({ lessonContext }) {
  const [isOpen, setIsOpen] = useState(false)
  const [input, setInput] = useState('')
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)
  const {
    messages, loading, remaining, error,
    sendMessage, resetConversation, isLoggedIn, isPremium
  } = useAiAssistant()

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) inputRef.current?.focus()
  }, [isOpen])

  // Reset conversation when lesson changes
  const prevLessonRef = useRef(lessonContext?.geezText)
  useEffect(() => {
    if (lessonContext?.geezText !== prevLessonRef.current) {
      prevLessonRef.current = lessonContext?.geezText
      resetConversation()
    }
  }, [lessonContext?.geezText, resetConversation])

  const handleSend = () => {
    if (!input.trim() || loading) return
    sendMessage(input, lessonContext)
    setInput('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <>
      {/* Toolbar assistant launcher */}
      <button
        className="ai-fab"
        onClick={() => setIsOpen(!isOpen)}
        title={t('aiAssistantTitle')}
        aria-label={t('aiAssistantTitle')}
        aria-expanded={isOpen}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <div className="ai-panel">
          <div className="ai-panel-header">
            <span className="ai-panel-title">{t('aiAssistantTitle')}</span>
            {remaining !== null && (
              <span className="ai-panel-remaining">
                {remaining > 0
                  ? `${remaining} ${t('aiRemaining')}`
                  : t('aiLimitReached')
                }
              </span>
            )}
          </div>

          <div className="ai-messages">
            {messages.length === 0 && !loading && (
              <div className="ai-welcome">
                {!isLoggedIn
                  ? t('aiSignInRequired')
                  : t('aiWelcome')
                }
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={i} className={`ai-message ai-message-${msg.role}`}>
                {msg.content}
              </div>
            ))}

            {loading && (
              <div className="ai-message ai-message-assistant ai-thinking">
                {t('aiThinking')}
              </div>
            )}

            {error === 'daily_limit' && (
              <div className="ai-message ai-message-system">
                {t('aiLimitReached')}
                {!isPremium && ` ${t('aiUpgradeHint')}`}
              </div>
            )}

            {error && error !== 'daily_limit' && (
              <div className="ai-message ai-message-system">
                {error}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="ai-input-bar">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('aiPlaceholder')}
              disabled={!isLoggedIn || loading || (remaining === 0 && error === 'daily_limit')}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || loading || !isLoggedIn}
              className="ai-send-btn"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
            </button>
          </div>
        </div>
      )}
    </>
  )
}
